import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const inputClass =
  `block w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900
   placeholder-neutral-400 shadow-sm transition focus:border-primary-600 focus:outline-none
   focus:ring-4 focus:ring-primary-100`;

const Field = ({ label, htmlFor, children, hint }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-neutral-800">
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-neutral-500">{hint}</p>}
  </div>
);

/**
 * Profile and password management for the signed-in user.
 *
 * The API (PUT /api/auth/profile, PUT /api/auth/change-password) and the
 * AuthContext helpers already existed; nothing in the UI called them, so users
 * - including tenants issued a temporary password - had no way to change their
 * own details or credentials.
 */
const ProfileSettings = () => {
  const { user, updateProfile, changePassword } = useAuth();

  const [profile, setProfile] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const onProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profile.first_name.trim() || !profile.last_name.trim()) {
      toast.error('First and last name are required');
      return;
    }
    setSavingProfile(true);
    try {
      await updateProfile(profile);
    } finally {
      setSavingProfile(false);
    }
  };

  const onPasswordSubmit = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (pw.new_password !== pw.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (pw.new_password === pw.current_password) {
      toast.error('Choose a password different from your current one');
      return;
    }
    setSavingPw(true);
    try {
      const result = await changePassword({
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      if (result && result.success) {
        setPw({ current_password: '', new_password: '', confirm_password: '' });
      }
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-neutral-200/70 bg-white p-6">
        <h3 className="text-lg font-medium text-neutral-900">Your profile</h3>
        <p className="mt-1 text-sm text-neutral-600">
          Signed in as <span className="font-medium text-neutral-800">{user && user.username}</span>
          {user && user.user_level ? ` · ${user.user_level}` : ''}
        </p>

        <form onSubmit={onProfileSubmit} className="mt-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First name" htmlFor="first_name">
              <input id="first_name" className={inputClass} value={profile.first_name}
                onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} />
            </Field>
            <Field label="Last name" htmlFor="last_name">
              <input id="last_name" className={inputClass} value={profile.last_name}
                onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Email" htmlFor="email">
              <input id="email" type="email" autoComplete="email" className={inputClass}
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })} />
            </Field>
            <Field label="Phone" htmlFor="phone" hint="Used for SMS rent reminders and payment receipts.">
              <input id="phone" type="tel" autoComplete="tel" className={inputClass}
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
            </Field>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={savingProfile}
              className="rounded-lg bg-primary-700 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-200 disabled:opacity-60">
              {savingProfile ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>

      <div className="rounded-xl border border-neutral-200/70 bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-medium text-neutral-900">Password</h3>
            <p className="mt-1 text-sm text-neutral-600">
              If you were given a temporary password, change it here.
            </p>
          </div>
          <button type="button" onClick={() => setShowPw((v) => !v)}
            aria-pressed={showPw}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-600 transition hover:text-neutral-900">
            {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {showPw ? 'Hide' : 'Show'}
          </button>
        </div>

        <form onSubmit={onPasswordSubmit} className="mt-5 space-y-4">
          <Field label="Current password" htmlFor="current_password">
            <input id="current_password" type={showPw ? 'text' : 'password'}
              autoComplete="current-password" className={inputClass}
              value={pw.current_password}
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="New password" htmlFor="new_password" hint="At least 6 characters.">
              <input id="new_password" type={showPw ? 'text' : 'password'}
                autoComplete="new-password" className={inputClass}
                value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
            </Field>
            <Field label="Confirm new password" htmlFor="confirm_password">
              <input id="confirm_password" type={showPw ? 'text' : 'password'}
                autoComplete="new-password" className={inputClass}
                value={pw.confirm_password}
                onChange={(e) => setPw({ ...pw, confirm_password: e.target.value })} />
            </Field>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={savingPw}
              className="rounded-lg bg-primary-700 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-200 disabled:opacity-60">
              {savingPw ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileSettings;
